import React from 'react';
import { lowerCase } from 'lodash';
import _isEmpty from 'lodash/isEmpty';

import { TFunction } from '@lyearn/i18n';
import { ErrorPosition, Fields, FieldType } from '@/components/Form';
import FrequencyFormField from '@/components/Form/components/Frequency';
import { validateScheduleInfo } from '@/components/Form/components/Frequency/utils';
import { UserProfile } from '@/modules/auth/types';

import AddParticipants from './AddParticipants';
import { MeetingFormType } from './types';

export const validateMeetingForm = (t: TFunction, values: MeetingFormType): any => {
  const errors: Partial<Record<keyof MeetingFormType, any>> = {};

  if (_isEmpty(values.participantIds)) {
    errors.participantIds = t('Meeting:ParticipantsError');
  }

  if ((values.participantIds ?? []).length < 2) {
    errors.participantIds = t('Meeting:Atleast2Participants');
  }

  if (_isEmpty(values.name)) {
    errors.name = t('Meeting:TitleError');
  }

  if (values.schedule) {
    const recurrenceScheduleErrorConfig = validateScheduleInfo(values.schedule);
    if (recurrenceScheduleErrorConfig) errors.schedule = recurrenceScheduleErrorConfig;
  }

  return errors;
};

export const getFields = ({
  t,
}: {
  t: TFunction;
  user?: UserProfile;
}): Fields<MeetingFormType> => ({
  name: {
    name: 'name',
    type: FieldType.Text,
    label: t('Meeting:Modal.SetupMeetingNameField'),
    placeholder: t('Meeting:Modal.SetupMeetingNameFieldPlaceholder'),
    size: 'large',
  },
  participantIds: {
    name: 'participantIds',
    type: FieldType.Custom,
    label: t('Meeting:Modal.SetupMeetingUserField'),
    Component: ({ value, onChange, error, touched }) => (
      <AddParticipants error={touched && error} value={value} onChange={onChange} />
    ),
  },
  schedule: {
    name: 'schedule',
    type: FieldType.Custom,
    errorPosition: ErrorPosition.Custom,
    Component: ({ value, onChange, error }) => {
      return (
        <FrequencyFormField
          errorConfig={error}
          moduleName={lowerCase(t('Meeting:Meeting'))}
          value={value}
          onChange={onChange}
        />
      );
    },
  },
});

export const translationMap = {
  reactivate: {
    title: 'Meeting:Modal.SetupMeetingReactivate',
    description: 'Meeting:Modal.SetupMeetingReactivateDescription',
    ctaButtonLabel: 'Common:Actions.Reactivate',
    snackbarText: 'Meeting:ReactivatedSuccess',
  },
  create: {
    title: 'Meeting:Modal.SetupMeetingTitle',
    description: 'Meeting:Modal.SetupMeetingDescription',
    ctaButtonLabel: 'Common:Actions.Create',
    snackbarText: 'Meeting:CreatedSuccess',
  },
  edit: {
    title: 'Meeting:Modal.SetupMeetingEditTitle',
    description: 'Meeting:Modal.SetupMeetingEditDescription',
    ctaButtonLabel: 'Common:Actions.Update',
    snackbarText: 'Meeting:UpdatedSuccess',
  },
};
